import React, { ReactNode, createContext, useEffect, useState, useContext } from "react";
import { User } from "oidc-client-ts";

import { userManager } from "../components/oidc-client-ts/userManager";

interface OidcUserContextType {
  oidcUser: User | null;
  loadingOidcUser: boolean;
  signinRedirect: (returnUrl: string) => void;
  signoutRedirect: () => void;
  clearStaleState: () => void;
}

type Props = {
  children: ReactNode;
};

const OidcUserContext = createContext<OidcUserContextType | null>(null);

export const OidcUserProvider: React.FC<Props> = ({ children }) => {
  const [loadingOidcUser, setLoadingOidcUser] = useState(true);
  const [oidcUser, setOidcUser] = useState<User | null>(null);

  useEffect(() => {
    userManager.getUser()
      .then((oidcUser) => {
        console.log("oidcUser:", oidcUser);
        setLoadingOidcUser(false);
        setOidcUser(oidcUser);
      })
      .catch(() => setLoadingOidcUser(false));

    const handleUserLoaded = (loadedUser: User) => {
      setOidcUser(loadedUser);
    };

    const handleSilentRenewError = (error: any) => {
      console.error('Silent renew failed', error);
      userManager.signinRedirect();
    };

    const handleUserSignedOut = () => {
      console.warn('User signed out at IdP');
      userManager.removeUser();
      setOidcUser(null);
    };

    const handleAccessTokenExpiring = () => {
      console.log("Access token is about to expire!");

      // Automatic silent renewal will happen in the background.
      // You don't need to manually refresh the token here.
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);
    userManager.events.addUserSignedOut(handleUserSignedOut);
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
    };
  }, []);

  const signinRedirect = (returnUrl: string) =>
    userManager.signinRedirect({ state: { returnUrl } });

  const signoutRedirect = () => userManager.signoutRedirect();

  const clearStaleState = () => userManager.clearStaleState();

  return (
    <OidcUserContext.Provider
      value={{
        oidcUser,
        loadingOidcUser,
        signinRedirect,
        signoutRedirect,
        clearStaleState,
      }}
    >
      {children}
    </OidcUserContext.Provider>
  );
};

export const useOidcUserContext = () => {
  const context = useContext(OidcUserContext);
  if (!context) throw new Error("useOidcUserContext must be used within OidcUserProvider");
  return context;
};